import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeft, Network, RefreshCw, Store, Users } from 'lucide-react';
import { supportAPI } from '../../services/supportApi';
import OrgStoresTab from '../org/OrgStoresTab';
import OrgMembersTab, { NewMemberDraft } from '../org/OrgMembersTab';
import { OrgMember, OrgStore, PriceListRef } from '../org/orgTypes';
import { SupportOrg } from './SupportNewStore';

/**
 * Support → «Αλυσίδες» (25/10/2026): οι ίδιες καρτέλες «Καταστήματα» και «Μέλη» που βλέπουν τα κεντρικά κάθε αλυσίδας
 * (OrgStoresTab / OrgMembersTab), με τις κλήσεις του /api/support και τα όριά του: ρόλοι μόνο «Διαχειριστής» / «Μόνο
 * προβολή», ο owner φαίνεται αλλά δεν αλλάζει, ο τιμοκατάλογος ρωτά πριν ξαναβάλει τιμές, χωρίς «Είσοδο» και «Κωδικούς»
 * (το support έχει την κάρτα καταστήματος). Νέα αλυσίδα: μόνο ο super admin.
 */

const SUPPORT_ROLES = ['org_viewer', 'org_admin'];
const LOCKED_ROLES = ['org_owner'];

interface OrgDetail {
  organization: { id: number; name: string };
  stores: OrgStore[];
  members: OrgMember[];
  price_lists: PriceListRef[];
}

const errText = (e: any, fallback: string) => e?.response?.data?.error || e?.response?.data?.message || fallback;
const catalogMode = (o: SupportOrg) => (o.catalog_mode === 'central' ? 'Κεντρικός κατάλογος' : 'Ανεξάρτητα καταστήματα');

const SupportChainsTab: React.FC = () => {
  const [orgs, setOrgs] = useState<SupportOrg[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [view, setView] = useState<'stores' | 'members'>('stores');
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const loadOrgs = async () => {
    setFailed(false);
    try { const d: any = await supportAPI.getOrganizations(); setOrgs(d?.organizations || []); } catch { setFailed(true); setOrgs([]); }
  };
  useEffect(() => { loadOrgs(); }, []);

  const open = async (id: number) => {
    setOrgId(id); setDetail(null); setView('stores'); setNote(null);
    try { setDetail(await supportAPI.getOrganization(id) as unknown as OrgDetail); } catch (e) { setNote({ ok: false, text: errText(e, 'Η αλυσίδα δεν φορτώθηκε.') }); }
  };
  const back = () => { setOrgId(null); setDetail(null); setNote(null); loadOrgs(); };

  /** Η απάντηση των αλλαγών φέρνει τα φρέσκα καταστήματα/μέλη. */
  const apply = (d: any) => setDetail(prev => (prev ? { ...prev, ...(d?.stores ? { stores: d.stores } : {}), ...(d?.members ? { members: d.members } : {}) } : prev));
  const plName = (id: number | null) => (id ? detail?.price_lists.find(pl => pl.id === id)?.name || `#${id}` : 'Βασικός');

  if (orgId == null) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="support-chains">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-t1 flex items-center gap-2">
              <Network className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />Αλυσίδες
            </h1>
            <p className="text-sm text-t3">Τα καταστήματα και οι χρήστες κάθε αλυσίδας. Νέα αλυσίδα φτιάχνει ο super admin.</p>
          </div>
          <button onClick={loadOrgs} className="p-2 text-t3 hover:text-t1 bg-surface-2 dark:bg-surface-1 border border-line rounded-lg" title="Ανανέωση">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        {orgs === null ? (
          <div className="flex items-center justify-center py-24"><RefreshCw className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>
        ) : failed ? (
          <p className="text-sm text-red-700 dark:text-red-300 text-center py-12">Οι αλυσίδες δεν φορτώθηκαν.</p>
        ) : orgs.length === 0 ? (
          <p className="text-sm text-t4 text-center py-12">Καμία αλυσίδα σε αυτόν τον server.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {orgs.map(o => (
              <button key={o.id} type="button" onClick={() => open(o.id)} data-testid={`support-chain-${o.id}`}
                className="text-left bg-surface-2 dark:bg-surface-1 border border-line hover:border-cyan-500/50 rounded-2xl p-4 transition-colors">
                <p className="text-sm font-semibold text-t1">{o.name} <span className="text-t4 font-normal">#{o.id}</span></p>
                <p className="text-xs text-t3 mt-0.5">{catalogMode(o)}{o.price_lists.length ? ` · ${o.price_lists.length} τιμοκατάλογοι` : ''}</p>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-t2">
                  <span className="inline-flex items-center gap-1"><Store className="h-3.5 w-3.5" />{o.store_count} καταστήματα</span>
                  <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{o.member_count ?? 0} χρήστες</span>
                </div>
                {!o.in_scheduler_scope && (
                  <p className="mt-2 inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="h-3.5 w-3.5" />Ο server δεν τρέχει τις αυτόματες εργασίες της
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="py-4 space-y-3" data-testid="support-chain-detail">
      <div className="px-4 sm:px-6 lg:px-8 flex flex-wrap items-center gap-3">
        <button type="button" onClick={back} className="inline-flex items-center gap-1.5 text-sm text-t3 hover:text-t1">
          <ArrowLeft className="h-4 w-4" />Αλυσίδες
        </button>
        <h1 className="text-xl font-bold text-t1">{detail?.organization.name || '…'}</h1>
        <div role="tablist" aria-label="Αλυσίδα" className="flex gap-1 ml-auto">
          {([['stores', 'Καταστήματα'], ['members', 'Χρήστες']] as const).map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => { setView(id); setNote(null); }} data-testid={`support-chain-view-${id}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg ${view === id ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300' : 'text-t3 hover:text-t1'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {note && (
        <p role="status" data-testid="support-chain-note"
          className={`mx-4 sm:mx-6 lg:mx-8 text-sm rounded-lg px-3 py-2 ${note.ok ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300' : 'bg-red-500/10 text-red-800 dark:text-red-300'}`}>
          {note.text}
        </p>
      )}
      {!detail ? (
        <div className="flex items-center justify-center py-24"><RefreshCw className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>
      ) : view === 'stores' ? (
        <OrgStoresTab
          orgId={orgId}
          stores={detail.stores}
          priceLists={detail.price_lists}
          canManage
          switching={null}
          showCredentials={false}
          confirmPriceList
          onSetStoreLabel={async (storeId, value) => {
            try {
              const r: any = await supportAPI.setOrgStoreLabel(orgId, storeId, value);
              setDetail(prev => (prev ? { ...prev, stores: prev.stores.map(s => (s.id === storeId ? { ...s, display_name: r.display_name } : s)) } : prev));
              setNote(null);
            } catch (e) { setNote({ ok: false, text: errText(e, 'Η ονομασία δεν αποθηκεύτηκε.') }); }
          }}
          onAssignPriceList={async (storeId, value) => {
            const plId = value ? Number(value) : null;
            try {
              await supportAPI.setOrgStorePriceList(orgId, storeId, plId);
              setDetail(prev => (prev ? { ...prev, stores: prev.stores.map(s => (s.id === storeId ? { ...s, price_list_id: plId } : s)) } : prev));
              setNote({ ok: true, text: `Οι τιμές του #${storeId} μπήκαν από τον τιμοκατάλογο «${plName(plId)}».` });
            } catch (e) { setNote({ ok: false, text: errText(e, 'Ο τιμοκατάλογος δεν άλλαξε.') }); }
          }}
          onAddStore={async (storeId, priceListId) => {
            try {
              const r: any = await supportAPI.addOrgStore(orgId, Number(storeId), priceListId ? Number(priceListId) : null);
              apply(r);
              const c = r?.catalog;
              setNote({ ok: !(c && c.reason === 'error'), text: `Το #${storeId} μπήκε στην αλυσίδα.${c?.pulled
                ? (c.pending ? ` Ο κατάλογος περιμένει έγκριση (${c.pending} αλλαγές — μπαίνουν στο πρώτο Ζ).` : ' Ο κατάλογος της αλυσίδας μπήκε.')
                : c?.reason === 'error' ? ` Ο κατάλογος ΔΕΝ ήρθε (${c.error}).` : ''}` });
              return null;
            } catch (e) { return errText(e, 'Η προσθήκη απέτυχε.'); }
          }}
          onRemoveStore={async (storeId) => {
            try { apply(await supportAPI.removeOrgStore(orgId, storeId)); setNote({ ok: true, text: `Το #${storeId} βγήκε από την αλυσίδα.` }); }
            catch (e) { setNote({ ok: false, text: errText(e, 'Η αφαίρεση απέτυχε.') }); }
          }}
        />
      ) : (
        <OrgMembersTab
          members={detail.members}
          stores={detail.stores}
          canManage
          roles={SUPPORT_ROLES}
          lockedRoles={LOCKED_ROLES}
          onUpdateRole={async (orgUserId, role) => {
            try { apply(await supportAPI.updateOrgMember(orgId, orgUserId, { role })); setNote(null); return true; }
            catch (e) { setNote({ ok: false, text: errText(e, 'Ο ρόλος δεν άλλαξε.') }); return false; }
          }}
          onSaveScope={async (member, ids) => {
            try {
              apply(await supportAPI.updateOrgMember(orgId, member.org_user_id, { role: member.role, store_ids: ids.length ? ids : null }));
              setNote(null);
              return true;
            } catch (e) { setNote({ ok: false, text: errText(e, 'Η πρόσβαση δεν αποθηκεύτηκε.') }); return false; }
          }}
          onRemove={async (orgUserId) => {
            try { apply(await supportAPI.removeOrgMember(orgId, orgUserId)); setNote(null); return true; }
            catch (e) { setNote({ ok: false, text: errText(e, 'Η αφαίρεση απέτυχε.') }); return false; }
          }}
          onAdd={async (draft) => {
            try {
              apply(await supportAPI.addOrgMember(orgId, { org_user_id: Number(draft.org_user_id), role: draft.role, store_ids: draft.store_ids.length ? draft.store_ids : null }));
              return null;
            } catch (e) { return errText(e, 'Η προσθήκη απέτυχε.'); }
          }}
          onCreate={async (draft: NewMemberDraft) => {
            try {
              apply(await supportAPI.createOrgMember(orgId, { ...draft, store_ids: draft.store_ids.length ? draft.store_ids : null }));
              setNote({ ok: true, text: `Ο χρήστης ${draft.email.trim().toLowerCase()} δημιουργήθηκε· δώσ' του το email και τον κωδικό.` });
              return null;
            } catch (e) { return errText(e, 'Η δημιουργία απέτυχε.'); }
          }}
          onResetPassword={async (member, password) => {
            try { await supportAPI.resetOrgUserPassword(member.org_user_id, password); return null; }
            catch (e) { return errText(e, 'Ο κωδικός δεν άλλαξε.'); }
          }}
        />
      )}
    </div>
  );
};

export default SupportChainsTab;
